/**
 * @module env
 * @since 2.0.0
 * @requires glob
 * @requires resolve
 * @requires memoizer
 * @requires html
 */

import { resolve } from '../../object';
import { memoizer } from '../../mixin';
import { getMetaTagContent } from '../../html';
import { glob } from '../glob';
import { isBrowserLike } from './__isBrowserLike';

/**
 * @class Env
 * @augments Memoiser
 * @classdesc Figure out details regarding the runtime environment. Isomorphic.
 * @property {Boolean} node        Is this environment a nodejs environment?
 * @property {Boolean} browser     Is this environment a browser or a browser-like environment?
 * @property {Boolean} development Is this environment defined as a development environment? @alias dev
 * @property {Boolean} production  Is this environment defined as a production environment? @alias prod
 * @property {Boolean} testing     Is this environment defined as a testing environment? @alias test
 * @property {Boolean} plike       Is this environment defined as a plike environment (production like)?
 *
 * @example
 * window.app = {environment: 'production'};
 * env.prod // true
 *
 * export NODE_ENV=production; npm start
 * env.prod // true
 */
class Env {
    constructor() {
        // getters and functions that should be memoised
        memoizer(this,
            'node',
            'browser',
            'development',
            'production',
            'testing',
            'plike',
            'toString'
        );
    }

    get node() {
        return Object.prototype.toString.call(typeof glob.process !== 'undefined' ? glob.process : 0) === '[object process]';
    }

    get browser() {
        return isBrowserLike() ||
                !this.node && [
                    typeof document,
                    typeof window
                ].every((item) => item !== 'undefined');
    }

    get development() {
        return this.toString().includes('dev');
    }

    get dev() {
        return this.development;
    }

    get production() {
        return this.toString().includes('prod');
    }

    get prod() {
        return this.production;
    }

    get testing() {
        return this.toString().includes('test');
    }

    get test() {
        return this.testing;
    }

    get plike() {
        return this.toString().includes('like');
    }

    /**
     * Returns application environment
     * Server: checks NODE_ENV process variable
     * Browser: checks global and falls back to head meta tag
     * @return {String} Value of environment set where applicable
     */
    toString() {
        const env = this.node ? resolve('process.env.NODE_ENV') : (resolve('app.environment') || getMetaTagContent('app_environment'));

        return (env || '').toLowerCase();
    }
}

export const env = new Env();
